import { useState, useEffect, useCallback } from 'react';
import { 
  Modal, 
  Descriptions, 
  Tag, 
  Button, 
  Space, 
  Divider, 
  Typography, 
  Alert,
  Spin,
  message,
  Form,
  Input
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  SendOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import apiClient from '../services/apiClient';
import { useAuth } from '../hooks/useAuth';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CostingRuleDetails = ({ 
  ruleId, 
  visible, 
  onClose, 
  onEdit, 
  onDelete, 
  onSubmitForApproval,
  onApprove,
  onReject,
  refreshData 
}) => {
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectForm] = Form.useForm();
  const { user } = useAuth();
  const userData = user?.data;

  const fetchRuleDetails = useCallback(async () => {
    if (!ruleId) return;

    setLoading(true);
    try {
      const response = await apiClient.get(`/api/costing-rules/${ruleId}`);
      setRule(response.data.data);
    } catch (error) {
      message.error('Failed to load rule details');
      // eslint-disable-next-line no-console
      // Error is handled by the UI state
    } finally {
      setLoading(false);
    }
  }, [ruleId]);

  useEffect(() => {
    if (visible && ruleId) {
      fetchRuleDetails();
    }
  }, [visible, ruleId, fetchRuleDetails]);

  const handleSubmitForApproval = async () => {
    setActionLoading(true);
    try {
      await apiClient.post(`/api/approvals/${ruleId}/submit`, {
        remarks: 'Submitted for approval'
      });
      message.success('Rule submitted for approval successfully');
      if (onSubmitForApproval) onSubmitForApproval();
      if (refreshData) refreshData();
      fetchRuleDetails(); // Refresh the details
    } catch (error) {
      message.error('Failed to submit rule for approval');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await apiClient.post(`/api/approvals/${ruleId}/approve`, {
        remarks: 'Approved'
      });
      message.success('Rule approved successfully');
      if (onApprove) onApprove();
      if (refreshData) refreshData();
      fetchRuleDetails(); // Refresh the details
    } catch (error) {
      message.error('Failed to approve rule');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = () => {
    rejectForm.resetFields();
    setRejectModalVisible(true);
  };

  const handleRejectSubmit = async () => {
    try {
      const values = await rejectForm.validateFields();
      setActionLoading(true);
      await apiClient.post(`/api/approvals/${ruleId}/reject`, {
        remarks: values.reason
      });
      message.success('Rule rejected successfully');
      setRejectModalVisible(false);
      if (onReject) onReject();
      if (refreshData) refreshData();
      fetchRuleDetails();
    } catch (error) {
      if (error.errorFields) return;
      message.error('Failed to reject rule');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'default',
      PENDING: 'processing',
      APPROVED: 'success',
      REJECTED: 'error',
      INACTIVE: 'default'
    };
    return colors[status] || 'default';
  };

  const getDisplayStatus = (rule) => {
    // If rule is inactive, show INACTIVE instead of actual status
    if (rule.isActive === false) {
      return 'INACTIVE';
    }
    return rule.status;
  };

  const canEdit = (rule) => {
    if (!rule || !userData) return false;

    // INACTIVE rules cannot be edited
    if (rule.isActive === false) return false;

    // Admin can edit any rule
    if (userData.role === 'Admin') return true;

    // For DRAFT rules, only the proposer can edit
    if (rule.status === 'DRAFT') {
      return rule.proposerId === userData.id;
    }

    // For APPROVED rules, users with entity access can edit as new versions
    if (rule.status === 'APPROVED') {
      return userData.accessibleEntities?.some(entity => entity.id === rule.entityId);
    }

    return false;
  };

  const canDelete = (rule) => {
    if (!rule || !userData) return false;

    // INACTIVE rules cannot be deleted
    if (rule.isActive === false) return false;

    return (userData.role === 'Admin') ||
           (rule.proposerId === userData.id && rule.status === 'DRAFT');
  };

  const canSubmitForApproval = (rule) => {
    if (!rule || !userData) return false;
    return rule.proposerId === userData.id && rule.status === 'DRAFT';
  };

  const canApprove = (rule) => {
    if (!rule || !userData) return false;
    return (userData.role === 'Admin' || userData.role === 'Manager') &&
           rule.status === 'PENDING' &&
           rule.proposerId !== userData.id;
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatDateWithTimezone = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const renderFeeStructure = (rule) => {
    const fees = [];

    if (rule.flatFee) {
      fees.push(`Flat Fee: ${formatCurrency(rule.flatFee, rule.feeCurrencyCode)}`);
    }

    if (rule.percentageRate) {
      fees.push(`Percentage: ${rule.percentageRate}%`);
    }

    if (rule.minimumFee) {
      fees.push(`Minimum: ${formatCurrency(rule.minimumFee, rule.feeCurrencyCode)}`);
    }

    if (rule.maximumFee) {
      fees.push(`Maximum: ${formatCurrency(rule.maximumFee, rule.feeCurrencyCode)}`);
    }

    return fees.length > 0 ? fees.join(' | ') : 'No fee structure defined';
  };

  const renderTieredPricing = (tieredPricing, currency) => {
    if (!tieredPricing) return null;

    const tiers = [];

    // Tier 1
    if (tieredPricing.tier1End || tieredPricing.tier1FixedFee || tieredPricing.tier1MDR) {
      const tier1Details = [];
      if (tieredPricing.tier1End) tier1Details.push(`Up to ${tieredPricing.tier1End}`);
      if (tieredPricing.tier1FixedFee) tier1Details.push(`Fixed: ${formatCurrency(tieredPricing.tier1FixedFee, currency)}`);
      if (tieredPricing.tier1MDR) tier1Details.push(`MDR: ${tieredPricing.tier1MDR}%`);
      tiers.push(`Tier 1: ${tier1Details.join(', ')}`);
    }

    // Tier 2
    if (tieredPricing.tier2End || tieredPricing.tier2FixedFee || tieredPricing.tier2MDR) {
      const tier2Details = [];
      if (tieredPricing.tier2End) tier2Details.push(`Up to ${tieredPricing.tier2End}`);
      if (tieredPricing.tier2FixedFee) tier2Details.push(`Fixed: ${formatCurrency(tieredPricing.tier2FixedFee, currency)}`);
      if (tieredPricing.tier2MDR) tier2Details.push(`MDR: ${tieredPricing.tier2MDR}%`);
      tiers.push(`Tier 2: ${tier2Details.join(', ')}`);
    }

    // Tier 3
    if (tieredPricing.tier3End || tieredPricing.tier3FixedFee || tieredPricing.tier3MDR) {
      const tier3Details = [];
      if (tieredPricing.tier3End) tier3Details.push(`Up to ${tieredPricing.tier3End}`);
      if (tieredPricing.tier3FixedFee) tier3Details.push(`Fixed: ${formatCurrency(tieredPricing.tier3FixedFee, currency)}`);
      if (tieredPricing.tier3MDR) tier3Details.push(`MDR: ${tieredPricing.tier3MDR}%`);
      tiers.push(`Tier 3: ${tier3Details.join(', ')}`);
    }

    return tiers.length > 0 ? tiers : null;
  };

  if (!rule && !loading) {
    return null;
  }

  return (
    <>
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Costing Rule Details
          </Title>
          {rule && (
            <Tag color={getStatusColor(getDisplayStatus(rule))} style={{ marginLeft: 8 }}>
              {getDisplayStatus(rule)}
            </Tag>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
    >
      <Spin spinning={loading}>
        {rule && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Entity" span={2}>
                <Text strong>{rule.entity?.name || 'N/A'}</Text>
                {rule.entity?.country && (
                  <Tag style={{ marginLeft: 8 }}>{rule.entity.country}</Tag>
                )}
              </Descriptions.Item>
              
              <Descriptions.Item label="Product">
                {rule.product?.name || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Channel">
                <div>
                  <Text strong>{rule.channel?.name || 'N/A'}</Text>
                  {rule.channel?.code && (
                    <div><Text code style={{ fontSize: '11px' }}>{rule.channel.code}</Text></div>
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Pricing Scheme">
                {rule.scheme?.code && rule.scheme?.description
                  ? `${rule.scheme.code} - ${rule.scheme.description}`
                  : rule.scheme?.code || rule.scheme?.description || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Fee Currency">
                {rule.feeCurrencyCode || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(getDisplayStatus(rule))}>{getDisplayStatus(rule)}</Tag>
              </Descriptions.Item>

              {/* Costing Details Section */}
              {rule.payoutRailName && (
                <Descriptions.Item label="Payout Rail">
                  {rule.payoutRailName}
                </Descriptions.Item>
              )}

              {rule.payoutConnectorName && (
                <Descriptions.Item label="Payout Connector">
                  {rule.payoutConnectorName}
                </Descriptions.Item>
              )}

              {rule.acquirerName && (
                <Descriptions.Item label="Acquirer">
                  {rule.acquirerName}
                </Descriptions.Item>
              )}

              {rule.cardBrandName && (
                <Descriptions.Item label="Card Brand">
                  {rule.cardBrandName}
                </Descriptions.Item>
              )}

              {rule.industryName && (
                <Descriptions.Item label="Industry">
                  {rule.industryName}
                </Descriptions.Item>
              )}

              {rule.businessId && (
                <Descriptions.Item label="Business ID">
                  {rule.businessId}
                </Descriptions.Item>
              )}

              {rule.cardType && rule.cardType !== 'N/A' && (
                <Descriptions.Item label="Card Type">
                  <Tag>{rule.cardType}</Tag>
                </Descriptions.Item>
              )}

              {rule.cardLocalForeign && rule.cardLocalForeign !== 'N/A' && (
                <Descriptions.Item label="Card Local/Foreign">
                  <Tag>{rule.cardLocalForeign}</Tag>
                </Descriptions.Item>
              )}

              {rule.cardTier && (
                <Descriptions.Item label="Card Tier">
                  {rule.cardTier}
                </Descriptions.Item>
              )}

              {rule.foreignLocalMerchant && rule.foreignLocalMerchant !== 'N/A' && (
                <Descriptions.Item label="Merchant Type">
                  <Tag>{rule.foreignLocalMerchant}</Tag>
                </Descriptions.Item>
              )}

              {rule.flow && (
                <Descriptions.Item label="Flow">
                  {rule.flow}
                </Descriptions.Item>
              )}

              {rule.fundSource && (
                <Descriptions.Item label="Fund Source">
                  {rule.fundSource}
                </Descriptions.Item>
              )}

              {rule.processor && (
                <Descriptions.Item label="Processor">
                  {rule.processor}
                </Descriptions.Item>
              )}

              {rule.onUs && (
                <Descriptions.Item label="On-Us">
                  {rule.onUs}
                </Descriptions.Item>
              )}

              {((rule.mccs && rule.mccs.length > 0) || rule.mcc) && (
                <Descriptions.Item label="MCCs">
                  {rule.mccs && rule.mccs.length > 0
                    ? rule.mccs.map((mccObj, index) => (
                      <Tag key={index}>{mccObj.mcc}</Tag>
                    ))
                    : <Text code>{rule.mcc}</Text>}
                </Descriptions.Item>
              )}

              {rule.sourceCurrencyCode && (
                <Descriptions.Item label="Source Currency">
                  {rule.sourceCurrencyCode}
                </Descriptions.Item>
              )}

              {rule.destinationCurrencyCode && (
                <Descriptions.Item label="Destination Currency">
                  {rule.destinationCurrencyCode}
                </Descriptions.Item>
              )}

              {rule.liquidityPartner && (
                <Descriptions.Item label="Liquidity Partner">
                  {rule.liquidityPartner}
                </Descriptions.Item>
              )}

              {rule.baseRateSource && (
                <Descriptions.Item label="Base Rate Source">
                  {rule.baseRateSource}
                </Descriptions.Item>
              )}

              {rule.bidSpreadBps != null && (
                <Descriptions.Item label="Bid Spread (bps)">
                  {rule.bidSpreadBps}
                </Descriptions.Item>
              )}

              {rule.askSpreadBps != null && (
                <Descriptions.Item label="Ask Spread (bps)">
                  {rule.askSpreadBps}
                </Descriptions.Item>
              )}

              {rule.offmarketSpreadBps != null && (
                <Descriptions.Item label="Off-Market Spread (bps)">
                  {rule.offmarketSpreadBps}
                </Descriptions.Item>
              )}

              {rule.weekendHolidaySpreadBps != null && (
                <Descriptions.Item label="Weekend/Holiday Spread (bps)">
                  {rule.weekendHolidaySpreadBps}
                </Descriptions.Item>
              )}

              {rule.valueDateConvention && (
                <Descriptions.Item label="Value Date Convention">
                  {rule.valueDateConvention}
                </Descriptions.Item>
              )}

              {rule.installmentTerm && (
                <Descriptions.Item label="Installment Term">
                  {rule.installmentTerm}
                </Descriptions.Item>
              )}

              {rule.installmentInterest !== null && rule.installmentInterest !== undefined && (
                <Descriptions.Item label="Installment Interest">
                  <Tag color={rule.installmentInterest ? 'green' : 'default'}>
                    {rule.installmentInterest ? 'Yes' : 'No'}
                  </Tag>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Effective Start Date">
                {formatDateWithTimezone(rule.effectiveStartDate)}
              </Descriptions.Item>

              <Descriptions.Item label="Effective End Date">
                {formatDateWithTimezone(rule.effectiveEndDate)}
              </Descriptions.Item>
              
              <Descriptions.Item label="Fee Structure" span={2}>
                <Text code>{renderFeeStructure(rule)}</Text>
              </Descriptions.Item>

              {rule.tieredPricing && (
                <Descriptions.Item label="Tiered Pricing Configuration" span={2}>
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <Text strong>Type: </Text>
                      <Tag color="blue">{rule.tieredPricing.tierAllOrStack}</Tag>
                      <Text strong style={{ marginLeft: '16px' }}>Model: </Text>
                      <Tag color="green">{rule.tieredPricing.tierCountModel}</Tag>
                    </div>
                    {renderTieredPricing(rule.tieredPricing, rule.feeCurrencyCode) && (
                      <div>
                        {renderTieredPricing(rule.tieredPricing, rule.feeCurrencyCode).map((tier, index) => (
                          <div key={index} style={{ marginBottom: '4px' }}>
                            <Text code style={{ fontSize: '12px' }}>{tier}</Text>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Include VAT">
                <Tag color={rule.includeVAT === 'TRUE' ? 'green' : rule.includeVAT === 'NON_VAT' ? 'default' : 'red'}>
                  {rule.includeVAT || 'N/A'}
                </Tag>
              </Descriptions.Item>

              {rule.includeVAT === 'TRUE' && (
                <Descriptions.Item label="VAT Rate">
                  {rule.vatRate != null ? `${parseFloat((rule.vatRate * 100).toFixed(4))}%` : 'N/A'}
                </Descriptions.Item>
              )}

              <Descriptions.Item label="MIDs" span={2}>
                {(() => {
                  if (rule.mids && rule.mids.length > 0) {
                    return (
                      <div>
                        {rule.mids.map((midObj, index) => (
                          <div key={index} style={{ marginBottom: '4px' }}>
                            <Text code style={{ fontSize: '12px' }}>
                              {midObj.mid}
                              {midObj.subMid && (
                                <Text type="secondary"> (Sub: {midObj.subMid})</Text>
                              )}
                            </Text>
                          </div>
                        ))}
                      </div>
                    );
                  } else if (rule.mid) {
                    // Fallback to legacy mid field
                    return (
                      <Text code style={{ fontSize: '12px' }}>
                        {rule.mid}
                        {rule.subMid && (
                          <Text type="secondary"> (Sub: {rule.subMid})</Text>
                        )}
                      </Text>
                    );
                  } else {
                    return 'N/A';
                  }
                })()}
              </Descriptions.Item>

              <Descriptions.Item label="Proposer Email">
                {rule.proposer?.email || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label="Approver Email">
                {rule.approver?.email || 'Not approved yet'}
              </Descriptions.Item>

              <Descriptions.Item label="Created At">
                {formatDateWithTimezone(rule.createdAt)}
              </Descriptions.Item>

              <Descriptions.Item label="Updated At">
                {formatDateWithTimezone(rule.updatedAt)}
              </Descriptions.Item>

              <Descriptions.Item label="Submitted At">
                {formatDateWithTimezone(rule.submittedAt)}
              </Descriptions.Item>

              <Descriptions.Item label="Approved At">
                {formatDateWithTimezone(rule.approvedAt)}
              </Descriptions.Item>

              <Descriptions.Item label="Rejected At">
                {formatDateWithTimezone(rule.rejectedAt)}
              </Descriptions.Item>

              {rule.rejectionReason && (
                <Descriptions.Item label="Rejection Reason" span={2}>
                  <Alert
                    message="Rejection Reason"
                    description={rule.rejectionReason}
                    type="warning"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                </Descriptions.Item>
              )}

              {rule.description && (
                <Descriptions.Item label="Description" span={2}>
                  {rule.description}
                </Descriptions.Item>
              )}
              
              {rule.submittedAt && (
                <Descriptions.Item label="Submitted At">
                  {new Date(rule.submittedAt).toLocaleString()}
                </Descriptions.Item>
              )}
              
              {rule.approvedAt && (
                <Descriptions.Item label="Approved At">
                  {new Date(rule.approvedAt).toLocaleString()}
                </Descriptions.Item>
              )}
              
              {rule.rejectedAt && (
                <Descriptions.Item label="Rejected At">
                  {new Date(rule.rejectedAt).toLocaleString()}
                </Descriptions.Item>
              )}
            </Descriptions>

            {rule.status === 'PENDING' && canApprove(rule) && (
              <Alert
                message="Approval Required"
                description="This rule is pending your approval. You can approve or reject it."
                type="info"
                showIcon
                style={{ margin: '16px 0' }}
              />
            )}

            <Divider />

            <Space>
              {canEdit(rule) && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => {
                    const editMode = rule.status === 'APPROVED' ? 'edit-as-new' : 'edit';
                    if (onEdit) {
                      onEdit(rule, editMode);
                    }
                  }}
                >
                  Update
                </Button>
              )}

              {canSubmitForApproval(rule) && (
                <Button 
                  type="default" 
                  icon={<SendOutlined />}
                  loading={actionLoading}
                  onClick={handleSubmitForApproval}
                >
                  Submit for Approval
                </Button>
              )}

              {canApprove(rule) && (
                <>
                  <Button 
                    type="primary" 
                    icon={<CheckCircleOutlined />}
                    loading={actionLoading}
                    onClick={handleApprove}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  >
                    Approve
                  </Button>
                  <Button 
                    danger 
                    icon={<CloseCircleOutlined />}
                    loading={actionLoading}
                    onClick={handleReject}
                  >
                    Reject
                  </Button>
                </>
              )}

              {canDelete(rule) && (
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete && onDelete(rule)}
                >
                  Delete
                </Button>
              )}
            </Space>
          </div>
        )}
      </Spin>
    </Modal>

    {/* Reject Reason Modal */}
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          Reject COGS Rule
        </Space>
      }
      open={rejectModalVisible}
      onOk={handleRejectSubmit}
      onCancel={() => {
        setRejectModalVisible(false);
        rejectForm.resetFields();
      }}
      confirmLoading={actionLoading}
      okText="Reject Rule"
      okButtonProps={{ danger: true }}
      cancelText="Cancel"
      width={600}
    >
      {rule && (
        <div>
          <div style={{ marginBottom: '16px' }}>
            <Text strong>Rule Details:</Text>
            <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
              <div><Text strong>Entity:</Text> {rule.entity?.name}</div>
              <div><Text strong>Product:</Text> {rule.product?.name}</div>
              <div><Text strong>Proposer:</Text> {rule.proposer?.email}</div>
              <div><Text strong>Fee Structure:</Text> {renderFeeStructure(rule)}</div>
            </div>
          </div>

          <Divider />

          <Form form={rejectForm} layout="vertical">
            <Form.Item
              name="reason"
              label="Rejection Reason"
              rules={[
                { required: true, message: 'Please provide a reason for rejection' },
                { min: 10, message: 'Reason must be at least 10 characters long' }
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Please provide a detailed reason for rejecting this COGS rule..."
                maxLength={1000}
                showCount
              />
            </Form.Item>
          </Form>
        </div>
      )}
    </Modal>
    </>
  );
};

export default CostingRuleDetails;
